import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "@/lib/constants";
import { REPORT_TYPES, PRIORITIES, RESPONSIBLE_PARTIES } from "@/lib/reconConstants";
import { FileText, FileSpreadsheet, FileDown, Eye, Loader2, ShieldAlert, Lock } from "lucide-react";

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${className}`}>{children}</span>
);

export default function ReconReports() {
  const [me, setMe] = useState(null);
  const [recons, setRecons] = useState([]);
  const [reconId, setReconId] = useState("");
  const [reportType, setReportType] = useState("executive");
  const [audience, setAudience] = useState("customer");
  const [families, setFamilies] = useState([]);
  const [famOptions, setFamOptions] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [openOnly, setOpenOnly] = useState(false);
  const [cover, setCover] = useState({ prepared_for: "", prepared_by: "", cover_note: "", footer: "" });
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState("");
  const [loading, setLoading] = useState(false);
  const caps = me?.caps || {};

  useEffect(() => { api.get("/recon/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  // Only finalized reconciliations (>=1 version) may be reported on.
  const finalized = useMemo(() => recons.filter((r) => (r.versions || []).length > 0), [recons]);
  const selectedRecon = finalized.find((r) => r.id === reconId);
  const latestVersion = selectedRecon ? (selectedRecon.versions || []).length : 0;

  useEffect(() => {
    api.get("/recon/reconciliations").then((r) => setRecons(r.data)).catch(() => {});
  }, []);

  // Default to the latest finalized reconciliation (most recently finalized).
  useEffect(() => {
    if (!reconId && finalized.length) {
      const sorted = [...finalized].sort((a, b) => (b.finalized_at || "").localeCompare(a.finalized_at || ""));
      setReconId(sorted[0].id);
    }
  }, [finalized, reconId]);

  // Load family options + reset preview when reconciliation changes.
  useEffect(() => {
    setPreview(null); setFamilies([]);
    if (!reconId) return;
    api.get(`/recon/reconciliations/${reconId}/controls`).then((r) => {
      const fams = Array.from(new Set((r.data || []).map((c) => (c.control_id || "").split(".").slice(0, 2).join(".")).filter(Boolean)));
      fams.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      setFamOptions(fams);
    }).catch(() => setFamOptions([]));
  }, [reconId]);

  const rtMeta = REPORT_TYPES.find((r) => r.value === reportType);
  useEffect(() => { if (rtMeta) setAudience(rtMeta.audience); }, [reportType]); // eslint-disable-line

  const body = useMemo(() => ({
    reconciliation_id: reconId, report_type: reportType, audience,
    families, priorities, orgs, open_only: openOnly,
    prepared_for: cover.prepared_for, prepared_by: cover.prepared_by,
    cover_note: cover.cover_note, footer: cover.footer, report_date: "",
  }), [reconId, reportType, audience, families, priorities, orgs, openOnly, cover]);

  const configKey = JSON.stringify(body);
  const previewIsCurrent = preview && previewKey === configKey;

  const toggle = (list, setList, val) => setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const runPreview = () => {
    if (!reconId) return toast.error("Select a finalized reconciliation.");
    setLoading(true);
    api.post("/recon/reports/preview", body).then((r) => {
      setPreview(r.data); setPreviewKey(configKey);
    }).catch((e) => toast.error(e.response?.data?.detail || "Preview failed.")).finally(() => setLoading(false));
  };

  const doExport = async (fmt) => {
    if (!caps.export) return toast.error("Your reconciliation role cannot export reports.");
    if (!previewIsCurrent) return toast.error("Generate a preview of the current settings before exporting.");
    try {
      const resp = await api.post(`/recon/reports/export?fmt=${fmt}`, body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      const disp = resp.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      a.href = url; a.download = m ? m[1] : `report.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}.`);
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  const noFinalized = finalized.length === 0;

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <FileText className="h-6 w-6 text-gold" aria-hidden="true" /> Readiness Reports
        </h1>
        <p className="text-slate-300 text-sm mt-2 max-w-3xl">Generate internal and customer-facing readiness reports from finalized reconciliations. Preview before exporting to PDF, Excel, or CSV.</p>
      </header>

      <div className="p-6 md:p-8">
        {noFinalized ? (
          <div className="rounded-md border border-border bg-card p-10 text-center text-muted-foreground" data-testid="reports-no-finalized">
            <Lock className="h-6 w-6 mx-auto mb-3 text-slate-400" />
            No finalized reconciliations available. Finalize a reconciliation to generate reports.
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          {/* Config panel */}
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-card p-4 space-y-4">
              <div className="space-y-1.5"><Label className="text-xs">Reconciliation (finalized only)</Label>
                <Select value={reconId} onValueChange={setReconId}>
                  <SelectTrigger data-testid="reports-recon"><SelectValue placeholder="Select reconciliation" /></SelectTrigger>
                  <SelectContent>
                    {finalized.map((r) => <SelectItem key={r.id} value={r.id}>{r.customer_name} — v{(r.versions || []).length} · {fmtDate(r.finalized_at)}</SelectItem>)}
                  </SelectContent>
                </Select>
                {selectedRecon && <p className="text-[11px] text-muted-foreground">Latest finalized version: v{latestVersion} · {selectedRecon.readiness || "—"}</p>}
              </div>

              <div className="space-y-1.5"><Label className="text-xs">Report type</Label>
                <Select value={reportType} onValueChange={(v) => { setReportType(v); setPreview(null); }}>
                  <SelectTrigger data-testid="reports-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">Customer-Facing</div>
                    {REPORT_TYPES.filter((r) => r.audience === "customer").map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">Internal</div>
                    {REPORT_TYPES.filter((r) => r.audience === "internal").map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Badge className={audience === "customer" ? "bg-gold/20 text-gold-dark border-gold/40" : "bg-navy/10 text-navy border-navy/20"}>
                  {audience === "customer" ? "Customer-Facing — internal notes hidden" : "Internal — includes internal notes"}
                </Badge>
              </div>

              {rtMeta?.audience === "internal" && (
                <div className="flex items-center justify-between rounded bg-slate-50 px-3 py-2">
                  <Label className="text-xs">Prepare as customer-facing (hide internal notes)</Label>
                  <Switch checked={audience === "customer"} onCheckedChange={(v) => { setAudience(v ? "customer" : "internal"); setPreview(null); }} data-testid="reports-audience-switch" />
                </div>
              )}
            </div>

            <div className="rounded-md border border-border bg-card p-4 space-y-3">
              <Label className="text-xs font-semibold text-navy uppercase tracking-wider">Filters</Label>
              <div className="space-y-1.5"><Label className="text-xs">Priorities</Label>
                <div className="flex flex-wrap gap-2">{PRIORITIES.map((p) => (
                  <label key={p} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={priorities.includes(p)} onCheckedChange={() => { toggle(priorities, setPriorities, p); setPreview(null); }} data-testid={`reports-prio-${p}`} />{p}
                  </label>))}</div></div>
              <div className="space-y-1.5"><Label className="text-xs">Responsible parties</Label>
                <div className="flex flex-wrap gap-2">{RESPONSIBLE_PARTIES.map((o) => (
                  <label key={o} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={orgs.includes(o)} onCheckedChange={() => { toggle(orgs, setOrgs, o); setPreview(null); }} data-testid={`reports-org-${o}`} />{o}
                  </label>))}</div></div>
              {famOptions.length > 0 && (
                <div className="space-y-1.5"><Label className="text-xs">Control families</Label>
                  <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">{famOptions.map((f) => (
                    <label key={f} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <Checkbox checked={families.includes(f)} onCheckedChange={() => { toggle(families, setFamilies, f); setPreview(null); }} />{f}
                    </label>))}</div></div>
              )}
              <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
                <Checkbox checked={openOnly} onCheckedChange={(v) => { setOpenOnly(!!v); setPreview(null); }} data-testid="reports-open-only" /> Open items only (for action-list reports)
              </label>
            </div>

            <div className="rounded-md border border-border bg-card p-4 space-y-3">
              <Label className="text-xs font-semibold text-navy uppercase tracking-wider">Cover</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Prepared for</Label><Input value={cover.prepared_for} onChange={(e) => { setCover({ ...cover, prepared_for: e.target.value }); setPreview(null); }} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Prepared by</Label><Input value={cover.prepared_by} onChange={(e) => { setCover({ ...cover, prepared_by: e.target.value }); setPreview(null); }} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Cover note</Label><Textarea rows={2} value={cover.cover_note} onChange={(e) => { setCover({ ...cover, cover_note: e.target.value }); setPreview(null); }} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Footer</Label><Input value={cover.footer} onChange={(e) => { setCover({ ...cover, footer: e.target.value }); setPreview(null); }} /></div>
            </div>

            <Button className="w-full bg-navy text-white hover:bg-navy/90" onClick={runPreview} disabled={loading} data-testid="reports-preview-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />} Generate Preview
            </Button>
          </div>

          {/* Preview panel */}
          <div className="rounded-md border border-border bg-card overflow-hidden flex flex-col">
            <div className="border-b border-border px-4 py-3 flex flex-wrap items-center justify-between gap-2 bg-slate-50">
              <div>
                <div className="font-semibold text-navy text-sm">{preview?.title || "Report preview"}</div>
                {previewIsCurrent && selectedRecon && (
                  <div className="text-[11px] text-muted-foreground">Reconciliation v{latestVersion} · generated {new Date().toLocaleString()} · {preview.row_count} rows</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={!previewIsCurrent || !caps.export} onClick={() => doExport("pdf")} data-testid="reports-export-pdf"><FileDown className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                <Button size="sm" variant="outline" disabled={!previewIsCurrent || !caps.export} onClick={() => doExport("xlsx")} data-testid="reports-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
                <Button size="sm" variant="outline" disabled={!previewIsCurrent || !caps.export} onClick={() => doExport("csv")} data-testid="reports-export-csv"><FileDown className="h-3.5 w-3.5 mr-1" /> CSV</Button>
              </div>
            </div>

            {!previewIsCurrent ? (
              <div className="flex-1 flex items-center justify-center p-16 text-center text-muted-foreground text-sm" data-testid="reports-preview-empty">
                {preview ? "Settings changed — generate a fresh preview before exporting." : "Configure the report and generate a preview. Export unlocks after previewing."}
              </div>
            ) : (
              <div className="p-4 space-y-4 overflow-auto">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  {preview.meta.filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="bg-slate-50 rounded px-2.5 py-1.5"><span className="text-muted-foreground">{k}: </span><span className="font-medium text-navy">{v}</span></div>
                  ))}
                </div>
                {preview.cover_note && <p className="text-sm italic text-slate-600">{preview.cover_note}</p>}
                <div className="overflow-x-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-navy text-white text-left">{preview.columns.map((c, i) => <th key={i} className="px-2.5 py-2 font-semibold whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>
                      {preview.rows_preview.length === 0 ? (
                        <tr><td colSpan={preview.columns.length} className="px-3 py-8 text-center text-muted-foreground">No matching rows for the selected filters.</td></tr>
                      ) : preview.rows_preview.map((row, ri) => (
                        <tr key={ri} className="border-b border-border last:border-0 odd:bg-white even:bg-slate-50/60">
                          {row.map((cell, ci) => <td key={ci} className="px-2.5 py-1.5 align-top">{String(cell ?? "")}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.row_count > preview.rows_preview.length && <p className="text-[11px] text-muted-foreground">Showing first {preview.rows_preview.length} of {preview.row_count} rows. Full data is included in the export.</p>}
                {preview.notes.map((n, i) => (
                  <p key={i} className="text-xs text-gold-dark flex items-start gap-1.5"><ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {n}</p>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
